import { Select } from '@mantine/core';
import { useTemplateEditorData } from 'pages/revisions-module/template-editor/TemplateEditorDataProvider';
import { useMemo } from 'react';

const DEFAULT_OPTIONS = [
  { value: 'serif', label: 'Serif' },
  { value: 'sans-serif', label: 'Sans-serif' },
  { value: 'monospace', label: 'Monospace' },
];

/**
 * Normalizes font-family value to a single canonical family name.
 *
 * Supports legacy values like `'Open Sans', sans-serif` and returns `Open Sans`.
 *
 * @param value Raw font-family value.
 * @returns Canonical font family name.
 */
function normalizeFontFamilyValue(value: string): string {
  return value.split(',')[0]?.replace(/["']/g, '').trim() ?? '';
}

/**
 * Select component for font family.
 */
export function FontFamilySelect({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { visual } = useTemplateEditorData();
  const normalizedValue = useMemo(() => normalizeFontFamilyValue(value), [value]);

  const options = useMemo(
    () => [
      ...DEFAULT_OPTIONS,
      ...(visual.googleFonts ?? []).map(({ fontName }) => ({
        value: fontName,
        label: `${fontName} (Google)`,
      })),
    ],
    [visual.googleFonts]
  );

  const styles = useMemo(
    () => ({
      item: options.reduce(
        (acc, { value: fontFamily }, i) => ({ ...acc, [`&:nth-of-type(${i + 1})`]: { fontFamily } }),
        {}
      ),
      input: { fontFamily: normalizedValue },
    }),
    [options, normalizedValue]
  );

  return (
    <Select
      size="md"
      searchable
      label="Písmo"
      styles={styles}
      value={normalizedValue}
      onChange={(value) => onChange(value as string)}
      data={options}
    />
  );
}
